import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  MaintenancePlanGroupForm,
  MaintenancePlanGroupFormValues,
} from "../../components/maintenance-plan-group-form";
import { Modal } from "../../components/modal";
import { useGetMaintenancePlanGroup } from "../../queries/maintenance-plan-group";
import {
  createMaintenancePlanCode,
  deleteMaintenancePlanCode,
} from "../../services/maintenance-plan-code";
import { updateMaintenancePlanGroup } from "../../services/maintenance-plan-group";
import { navigateBack } from "../../utils/navigate-back";

export function MaintenancePlanGroupEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("Maintenance plan group ID is required");
  }

  const { data: group, isLoading: isLoadingGroup } =
    useGetMaintenancePlanGroup(id);
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [codeError, setCodeError] = useState(false);
  const [formValues, setFormValues] = useState<MaintenancePlanGroupFormValues>({
    name: "",
  });

  useEffect(() => {
    if (group) {
      setFormValues({ name: group.name });
    }
  }, [group]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/maintenance/groups");
  };

  const invalidateGroups = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["maintenancePlanGroup", id] }),
      queryClient.invalidateQueries({ queryKey: ["maintenancePlanGroups"] }),
      queryClient.invalidateQueries({ queryKey: ["maintenancePlans"] }),
    ]);
  };

  return (
    <Modal open onClose={closeModal}>
      <MaintenancePlanGroupForm
        isEdit
        codeError={codeError}
        codes={group?.codes ?? []}
        hasError={hasError}
        loading={isLoadingGroup || loading}
        values={formValues}
        onAddCode={async (code) => {
          setCodeError(false);
          try {
            await createMaintenancePlanCode({
              code,
              maintenancePlanGroup: id,
            });
            await invalidateGroups();
          } catch {
            setCodeError(true);
            throw new Error("Failed to add code");
          }
        }}
        onCancel={closeModal}
        onChange={setFormValues}
        onRemoveCode={async (codeId) => {
          await deleteMaintenancePlanCode(codeId);
          await invalidateGroups();
        }}
        onSubmit={() => {
          void (async () => {
            setLoading(true);
            setHasError(false);
            try {
              await updateMaintenancePlanGroup(id, { name: formValues.name });
              await invalidateGroups();
              closeModal();
            } catch {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
